/**
 * ScopeDeleteModal — 2-step confirmation modal for scope cascade-delete.
 *
 * Step 1 (Preview):  Runs dry-run cleanup (backend returns matched counts + GCS URIs).
 * Step 2 (Confirm):  User types "DELETE" → sequentially deletes GCS objects → DB records.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useEscapeLayer } from '@/shared/hooks/useEscapeLayer';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2, AlertTriangle, Loader2, CheckCircle2, XCircle } from 'lucide-react';

import {
  workflowDataCleanupApi,
  gcsDeleteApi,
  type WorkflowDataCleanupResponse,
  type GcsDeleteResponse,
} from '@/features/system/orchestrator/api/cleanupApi';

/* ── Props ────────────────────────────────────────────────────── */

export interface ScopeDeleteModalProps {
  open: boolean;
  onClose: () => void;
  scopeNamespace: string;
  scopeKey: string;
  /** Friendly display name for the scope (decoded slug) */
  scopeLabel: string;
  /** @deprecated – GCS URIs now come from the dry-run response. */
  idScope?: number;
}

/* ── Component ────────────────────────────────────────────────── */

type Phase = 'loading' | 'preview' | 'executing' | 'done' | 'error';

export default function ScopeDeleteModal({
  open,
  onClose,
  scopeNamespace,
  scopeKey,
  scopeLabel,
  idScope,
}: ScopeDeleteModalProps) {
  const queryClient = useQueryClient();

  /* ── local state ───────────────────────────────────────────── */
  const [phase, setPhase] = useState<Phase>('loading');
  const [confirmText, setConfirmText] = useState('');
  const [dryRunResult, setDryRunResult] = useState<WorkflowDataCleanupResponse | null>(null);
  const [gcsUris, setGcsUris] = useState<string[]>([]);
  const [gcsResult, setGcsResult] = useState<GcsDeleteResponse | null>(null);
  const [cleanupResult, setCleanupResult] = useState<WorkflowDataCleanupResponse | null>(null);
  const [errorMsg, setErrorMsg] = useState('');

  /* ── Step 1: load preview on open ──────────────────────────── */
  useEffect(() => {
    if (!open) return;
    // Reset state on each open.
    setPhase('loading');
    setConfirmText('');
    setDryRunResult(null);
    setGcsUris([]);
    setGcsResult(null);
    setCleanupResult(null);
    setErrorMsg('');

    let cancelled = false;

    (async () => {
      try {
        // 1a) Dry-run cleanup to get matched counts.
        const dryRun = await workflowDataCleanupApi({
          dry_run: true,
          filters: { scope_namespace: scopeNamespace, scope_key: scopeKey },
          targets: { runs: true, scopes: true },
        });
        if (cancelled) return;
        setDryRunResult(dryRun);

        // 1b) GCS URIs are now returned by the backend dry-run response.
        if (cancelled) return;
        setGcsUris(dryRun.gcs_uris ?? []);
        setPhase('preview');
      } catch (e: any) {
        if (cancelled) return;
        setErrorMsg(e?.message ?? String(e));
        setPhase('error');
      }
    })();

    return () => { cancelled = true; };
  }, [open, scopeNamespace, scopeKey, idScope]);

  /* ── Step 2: execute delete ────────────────────────────────── */
  const executeMutation = useMutation({
    mutationFn: async () => {
      // 2a) Delete GCS objects first (if any).
      let gcsRes: GcsDeleteResponse | null = null;
      if (gcsUris.length > 0) {
        gcsRes = await gcsDeleteApi(gcsUris);
      }
      setGcsResult(gcsRes);

      // 2b) Delete DB records via cleanup API.
      const cleanupRes = await workflowDataCleanupApi({
        dry_run: false,
        filters: { scope_namespace: scopeNamespace, scope_key: scopeKey },
        targets: { runs: true, scopes: true },
      });
      setCleanupResult(cleanupRes);

      return { gcsRes, cleanupRes };
    },
    onSuccess: () => {
      setPhase('done');
      // Invalidate relevant queries.
      queryClient.invalidateQueries({ queryKey: ['developer', 'scopeNamespaces'] });
      queryClient.invalidateQueries({ queryKey: ['developer', 'scopeKeys'] });
      queryClient.invalidateQueries({ queryKey: ['developer', 'workflowScopes'] });
    },
    onError: (e: any) => {
      setErrorMsg(e?.message ?? String(e));
      setPhase('error');
    },
  });

  const handleExecute = useCallback(() => {
    setPhase('executing');
    executeMutation.mutate();
  }, [executeMutation]);

  /* ── Keyboard: Escape to close ─────────────────────────────── */
  useEscapeLayer(onClose, open && phase !== 'executing');

  /* ── Render ────────────────────────────────────────────────── */
  if (!open) return null;

  const matched = dryRunResult?.matched;
  const totalRecords = matched
    ? matched.runs + matched.steps + matched.executions + matched.outcomes + matched.run_outcomes + matched.scopes
    : 0;

  const canExecute = confirmText === 'DELETE';

  return (
    /* Backdrop */
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
      {/* Modal */}
      <div className="w-full max-w-lg bg-black rounded-lg border border-neutral-800 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-neutral-800 flex items-center gap-3">
          <div className="p-2 rounded-full bg-rose-950/60">
            <Trash2 className="w-5 h-5 text-rose-400" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-neutral-100">Delete Scope</h2>
            <p className="text-xs text-neutral-400 mt-0.5 truncate max-w-[360px]" title={scopeLabel}>
              {scopeLabel}
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* ─ Phase: loading ─ */}
          {phase === 'loading' && (
            <div className="flex items-center gap-2 text-sm text-neutral-400 py-6 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" />
              Scanning scope data…
            </div>
          )}

          {/* ─ Phase: preview ─ */}
          {phase === 'preview' && (
            <>
              <div className="flex items-start gap-2 p-3 rounded-md bg-amber-950/30 border border-amber-800/40">
                <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                <div className="text-xs text-amber-200 leading-relaxed">
                  This action is <strong>irreversible</strong>. All runs, steps, executions, outcomes,
                  and GCS artifacts for this scope will be permanently deleted.
                </div>
              </div>

              {/* Record counts */}
              {matched && (
                <div className="space-y-1.5">
                  <div className="text-xs font-medium text-neutral-300">Database records to delete</div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    {([
                      ['Runs', matched.runs],
                      ['Steps', matched.steps],
                      ['Executions', matched.executions],
                      ['Outcomes', matched.outcomes],
                      ['Run outcomes', matched.run_outcomes],
                      ['Scopes', matched.scopes],
                    ] as const).map(([label, count]) => (
                      <div key={label} className="pac-glass-subtle rounded px-2 py-1.5">
                        <span className="text-neutral-500">{label}: </span>
                        <span className={count > 0 ? 'text-rose-300 font-medium' : 'text-neutral-500'}>{count}</span>
                      </div>
                    ))}
                  </div>
                  <div className="text-[10px] text-neutral-500 pt-1">
                    Total: <span className="text-neutral-300">{totalRecords}</span> records
                  </div>
                </div>
              )}

              {/* GCS URIs */}
              <div className="space-y-1.5">
                <div className="text-xs font-medium text-neutral-300">
                  GCS artifacts: <span className={gcsUris.length > 0 ? 'text-rose-300' : 'text-neutral-500'}>{gcsUris.length}</span>
                </div>
                {gcsUris.length > 0 && (
                  <div className="max-h-32 overflow-y-auto rounded border border-neutral-800 p-2 space-y-0.5">
                    {gcsUris.map((uri, i) => (
                      <div key={i} className="text-[10px] text-neutral-400 font-mono truncate" title={uri}>
                        {uri}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Confirmation input */}
              <div className="space-y-2 pt-2">
                <label className="text-xs text-neutral-400">
                  Type <span className="font-mono font-bold text-rose-400">DELETE</span> to confirm:
                </label>
                <input
                  type="text"
                  autoFocus
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  className="w-full pac-glass-input rounded px-3 py-2 text-sm text-neutral-100 font-mono placeholder-neutral-600 outline-none focus:border-rose-600 transition-colors"
                  placeholder="DELETE"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && canExecute) handleExecute();
                  }}
                />
              </div>
            </>
          )}

          {/* ─ Phase: executing ─ */}
          {phase === 'executing' && (
            <div className="flex flex-col items-center gap-3 py-6">
              <Loader2 className="w-6 h-6 animate-spin text-rose-400" />
              <div className="text-sm text-neutral-300">
                {!gcsResult && gcsUris.length > 0
                  ? `Deleting ${gcsUris.length} GCS artifact${gcsUris.length !== 1 ? 's' : ''}…`
                  : 'Cleaning up database records…'}
              </div>
            </div>
          )}

          {/* ─ Phase: done ─ */}
          {phase === 'done' && (
            <div className="space-y-3 py-3">
              <div className="flex items-center gap-2 text-sm text-emerald-400">
                <CheckCircle2 className="w-5 h-5" />
                Scope deleted successfully
              </div>

              {gcsResult && (
                <div className="text-xs text-neutral-400 space-y-0.5">
                  <div>GCS deleted: <span className="text-neutral-200">{gcsResult.deleted.length}</span></div>
                  {gcsResult.failed.length > 0 && (
                    <div className="text-amber-400">GCS failed: {gcsResult.failed.length}</div>
                  )}
                  {gcsResult.skipped.length > 0 && (
                    <div>GCS skipped: {gcsResult.skipped.length}</div>
                  )}
                </div>
              )}

              {cleanupResult && (
                <div className="text-xs text-neutral-400 space-y-0.5">
                  <div>Runs deleted: <span className="text-neutral-200">{cleanupResult.deleted.runs}</span></div>
                  <div>Steps deleted: <span className="text-neutral-200">{cleanupResult.deleted.steps}</span></div>
                  <div>Executions deleted: <span className="text-neutral-200">{cleanupResult.deleted.executions}</span></div>
                  <div>Outcomes deleted: <span className="text-neutral-200">{cleanupResult.deleted.outcomes}</span></div>
                  <div>Run outcomes deleted: <span className="text-neutral-200">{cleanupResult.deleted.run_outcomes}</span></div>
                  <div>Scopes deleted: <span className="text-neutral-200">{cleanupResult.deleted.scopes}</span></div>
                </div>
              )}
            </div>
          )}

          {/* ─ Phase: error ─ */}
          {phase === 'error' && (
            <div className="space-y-2 py-3">
              <div className="flex items-center gap-2 text-sm text-rose-400">
                <XCircle className="w-5 h-5" />
                Delete failed
              </div>
              <div className="text-xs text-neutral-400 break-all">{errorMsg}</div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-neutral-800 flex justify-end gap-2">
          {(phase === 'preview' || phase === 'error') && (
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-1.5 rounded text-xs text-neutral-300 bg-black border border-neutral-700 hover:bg-rose-600 hover:border-rose-600 hover:text-white transition-colors"
            >
              Cancel
            </button>
          )}
          {phase === 'preview' && (
            <button
              type="button"
              disabled={!canExecute}
              onClick={handleExecute}
              className={
                'px-4 py-1.5 rounded text-xs font-medium transition-colors ' +
                (canExecute
                  ? 'bg-black border border-neutral-700 text-neutral-300 hover:bg-rose-600 hover:border-rose-600 hover:text-white'
                  : 'bg-black border border-neutral-700 text-neutral-600 cursor-not-allowed')
              }
            >
              Delete scope
            </button>
          )}
          {phase === 'done' && (
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-1.5 rounded text-xs text-neutral-300 bg-transparent border border-neutral-700 hover:border-neutral-500 transition-colors"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
